import dayjs from "dayjs";
// type-only: avoids a runtime cycle (Organisation → ConsultantFilter → this file)
import type Organisation from "../models/Organisation";
import type { RequirementRef } from "../models/Organisation";
import type { ConsultantTag } from "../models/Consultant";

/**
 * The shared validity rule for consultant certifications/tags:
 * valid = no `expiresAt` (null/undefined) OR `expiresAt` in the future.
 * Expired certifications are shown (marked "Utgången"); expired tags count
 * as unassigned and are hidden entirely.
 */
export function isCredentialValid(expiresAt?: string | null): boolean {
	return expiresAt == null || dayjs(expiresAt).isAfter(dayjs());
}

/**
 * Default expiry (YYYY-MM-DD) for a newly assigned CERTIFICATION whose type
 * defines a `validityDuration` in DAYS. No duration → no default expiry.
 * The date is editable per consultant before assigning.
 */
export function defaultExpiryFromValidity(validityDuration?: number): string | undefined {
	if (!validityDuration || validityDuration <= 0) return undefined;
	return dayjs().add(validityDuration, "day").format("YYYY-MM-DD");
}

/**
 * Computed expiry (full ISO instant) for a newly assigned TAG whose type
 * defines a `validityDuration` in HOURS. No duration → null (never expires).
 * Tag expiry is defined only at org tag-type level — never edited per
 * consultant.
 */
export function tagExpiryFromValidity(validityDuration?: number): string | null {
	if (!validityDuration || validityDuration <= 0) return null;
	return dayjs().add(validityDuration, "hour").toISOString();
}

/**
 * The next `profile.tags` after toggling a tag type on a consultant: removed
 * when validly held, otherwise (re)assigned with expiry from the type's
 * hours-based validity (stale expired entries for the key are dropped).
 */
export function toggleConsultantTag(
	current: ConsultantTag[],
	tagType: { key: string; validityDuration?: number }
): ConsultantTag[] {
	const isAssigned = current.some(
		(tag) => tag.key === tagType.key && isCredentialValid(tag.expiresAt)
	);
	const withoutKey = current.filter((tag) => tag.key !== tagType.key);

	if (isAssigned) return withoutKey;

	return [
		...withoutKey,
		{
			key: tagType.key,
			assignedAt: new Date().toISOString(),
			expiresAt: tagExpiryFromValidity(tagType.validityDuration),
		},
	];
}

/**
 * Re-apply a tag on a consultant: any entry for the key is dropped and a fresh
 * one assigned, so a timed tag's clock restarts from now. Unlike
 * {@link toggleConsultantTag} this never removes.
 */
export function renewConsultantTag(
	current: ConsultantTag[],
	tagType: { key: string; validityDuration?: number }
): ConsultantTag[] {
	return [
		...current.filter((tag) => tag.key !== tagType.key),
		{
			key: tagType.key,
			assignedAt: new Date().toISOString(),
			expiresAt: tagExpiryFromValidity(tagType.validityDuration),
		},
	];
}

/** The next `profile.tags` with every entry for the key removed. */
export function removeConsultantTag(current: ConsultantTag[], key: string): ConsultantTag[] {
	return current.filter((tag) => tag.key !== key);
}

/**
 * Whole hours left until a tag expires (rounded up). `null` when the tag never
 * expires or has already expired.
 */
export function tagHoursLeft(expiresAt?: string | null): number | null {
	if (!expiresAt) return null;
	const msLeft = dayjs(expiresAt).diff(dayjs());
	return msLeft > 0 ? Math.ceil(msLeft / 3_600_000) : null;
}

/** Display name for a certification type key, falling back to the key itself. */
export function certificationTypeName(organisation: Organisation | undefined, typeKey: string) {
	return organisation?.certificationTypes?.find((c) => c.key === typeKey)?.name?.sv ?? typeKey;
}

/** Display name for a consultant tag key, falling back to the key itself. */
export function consultantTagName(organisation: Organisation | undefined, key: string) {
	return organisation?.consultantTagTypes?.find((c) => c.key === key)?.name?.sv ?? key;
}

/**
 * Human-readable labels for a job's missing requirements (the admin matching
 * list's `missingRequirements` flag), resolving display names from the org
 * taxonomies and falling back to the raw key.
 */
export function missingRequirementLabels(
	organisation: Organisation | undefined,
	missing: RequirementRef[] | undefined
): string[] {
	return (missing ?? []).map((req) =>
		req.kind === "certification"
			? certificationTypeName(organisation, req.key)
			: consultantTagName(organisation, req.key)
	);
}
